import {ApproverGroup, ApproverGroupFormValues} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useMobileBreakPoint} from '@hconnect/common/hooks/useWidth'
import {Check, Close} from '@mui/icons-material'
import {LoadingButton} from '@mui/lab'
import {Box, Button, Grid, Theme, Typography, useMediaQuery} from '@mui/material'
import {UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {FC, Fragment} from 'react'
import {Control, Controller, UseFormHandleSubmit} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../../consts'
import {UserSearchField} from '../../eventProperties/UserSearchField'

type ApproverGroupsProps = {
  plantId: string
  onClose: () => void
  isPending: boolean
  approverGroupsQuery: UseQueryResult<ApproverGroup[], AxiosError>
  currentValues: ApproverGroup[]
  control: Control<ApproverGroupFormValues>
  handleSubmit: UseFormHandleSubmit<ApproverGroupFormValues>
  onSubmit: (values: ApproverGroupFormValues) => void
}

export const ApproverGroups: FC<ApproverGroupsProps> = ({
  plantId,
  onClose,
  approverGroupsQuery: {data, isLoading, isError, refetch},
  isPending,
  currentValues,
  handleSubmit,
  onSubmit,
  control
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const isMobile = useMobileBreakPoint()
  const isMobileLarge = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      display="flex"
      flexDirection="column"
      overflow="auto"
      gap={2}
    >
      <DataContentWrapper
        showLoader={isLoading}
        data={data}
        isError={isError}
        progressSx={{color: 'primary.main'}}
        retryFunction={refetch}
      >
        <Grid container alignItems="center" spacing={2} overflow="auto" pt={isMobile ? 0.5 : 0}>
          {currentValues?.map((group, index) => (
            <Fragment key={group.id}>
              <Grid item xs={12} sm={5}>
                <Typography
                  pt={isMobile ? 1.5 : 0}
                  variant={isMobile ? 'h4' : undefined}
                  data-test-id={`approver-group-name-${group.id}`}
                >
                  {group.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Controller
                  name={`groups.${index}.approvers`}
                  control={control}
                  render={({field}) => (
                    <UserSearchField
                      plantId={plantId}
                      label={t('approversAdjust.label.approvers', 'Approvers')}
                      value={field.value || []}
                      onChange={(users) => field.onChange(users || [])}
                      data-test-id={`approver-group-users-${group.id}`}
                      sx={{width: '100%'}}
                    />
                  )}
                />
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </DataContentWrapper>
      <Box
        display="flex"
        flexDirection={{xs: 'column', md: 'row'}}
        justifyContent="flex-end"
        mt={1.5}
        gap={1}
      >
        {isMobileLarge && (
          <Button
            onClick={onClose}
            variant="outlined"
            data-test-id="approver-group-close"
            fullWidth={isMobileLarge}
            color="secondary"
            startIcon={<Close />}
          >
            {t('approversAdjust.action.close')}
          </Button>
        )}
        <LoadingButton
          loading={isPending}
          type="submit"
          variant="contained"
          data-test-id="approver-group-save"
          fullWidth={isMobileLarge}
          startIcon={<Check />}
        >
          {t('approversAdjust.action.done')}
        </LoadingButton>
      </Box>
    </Box>
  )
}
